import { ChangeEvent, FC, useContext, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'

import { useTranslation } from 'react-i18next'
import MDEditor from '@uiw/react-md-editor'
import { Props } from './types'
import { TagInput, TextArea, ToolTip } from '~/shared/ui'
import { useStore } from '~/app/store'
import { Input } from '~/shared/ui/input'
import { TabsValidationContext } from '~/shared/lib/contexts/tabs-validation-context/provider'
import {
  getFieldError,
  getInputStatus,
} from '~/shared/lib/contexts/tabs-validation-context/helpers/validation-helpers'

export const CreateCategoryForm: FC<Props> = observer(({ locale }) => {
  const { t } = useTranslation()
  const { categoriesStore } = useStore()
  const { setCreateCategoryData, createCategoryData, setLocale } =
    categoriesStore
  const { error, clearFieldError } = useContext(TabsValidationContext)

  const nameFieldPath = `localization.${locale}.name`

  useEffect(() => {
    setLocale(locale)
  }, [setLocale, locale])

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { target } = e

    setCreateCategoryData(
      {
        [locale]: {
          ...createCategoryData.localization[locale],
          [target.name]: target.value,
        },
      },
      'localization',
    )
  }

  const handleTags = (value: string[]) => {
    setCreateCategoryData(
      {
        [locale]: {
          ...createCategoryData.localization[locale],
          seoKeywords: value.join(','),
        },
      },
      'localization',
    )
  }

  const handleChangeMD = (e: string | undefined) => {
    setCreateCategoryData(
      {
        [locale]: {
          ...createCategoryData.localization[locale],
          description: e,
        },
      },
      'localization',
    )
  }

  const defaultKeywords = useMemo(() => {
    const arrOfWords =
      createCategoryData.localization[locale]?.seoKeywords?.split(',')

    return arrOfWords?.map((item) => item.trim()).filter(Boolean)
  }, [createCategoryData.localization, locale])

  return (
    <form className="rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-lg text-title-color font-medium">{t('name2')}</h2>

        <ToolTip
          tooltipText={t('categoryNameTooltipText')}
          tooltipTitle={t('categoryNameTooltipTitle')}
        />
      </div>

      <div className="flex flex-col gap-3">
        <Input
          label={t('categoryName')}
          labelType="floating"
          name="name"
          id="name"
          value={createCategoryData.localization[locale]?.name || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleChange(e)

            if (e.target.value) {
              clearFieldError(nameFieldPath)
            }
          }}
          status={getInputStatus(error, nameFieldPath)}
          helperText={getFieldError(error, nameFieldPath)}
        />

        <MDEditor
          value={createCategoryData.localization[locale]?.description || ''}
          onChange={(value) => handleChangeMD(value)}
        />
      </div>

      <hr className="my-4" />

      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-lg text-title-color font-medium">Seo</h3>
        <ToolTip
          tooltipText={t('categorySeoSectionTooltipText')}
          tooltipTitle={t('categorySeoSectionTooltipTitle')}
        />
      </div>

      <div className="flex flex-col gap-3">
        <Input
          label={t('name2')}
          labelType="floating"
          name="seoTitle"
          id="seoTitle"
          value={createCategoryData.localization[locale]?.seoTitle || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
          renderIconEnd={
            <ToolTip
              tooltipText={t('categorySeoTitleTooltipText')}
              tooltipTitle={t('categorySeoTitleTooltipTitle')}
            />
          }
        />
        {(createCategoryData.localization[locale]?.seoDescription || '')
          .length > 50 ? (
          <TextArea
            label={t('description')}
            name="seoDescription"
            id="seoDescription"
            onChange={handleChange}
            value={
              createCategoryData.localization[locale]?.seoDescription || ''
            }
          />
        ) : (
          <Input
            label={t('description')}
            labelType="floating"
            id="seoDescription"
            name="seoDescription"
            value={
              createCategoryData.localization[locale]?.seoDescription || ''
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
            renderIconEnd={
              <ToolTip
                tooltipText={t('categorySeoDescriptionTooltipText')}
                tooltipTitle={t('categorySeoDescriptionTooltipTitle')}
              />
            }
          />
        )}
        <TagInput
          defaultData={defaultKeywords}
          placeholder={t('keyWords')}
          onChange={handleTags}
          renderIconEnd={
            <ToolTip
              tooltipText={t('categoryKeywordsTooltipText')}
              tooltipTitle={t('categoryKeywordsTooltipTitle')}
            />
          }
        />
      </div>
    </form>
  )
})
